import { PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { KnowledgeBaseArticle } from '@hefe/models';
import { KnowledgeBaseApi, currentUserIsAdminSelector } from '@hefe/store';
import { formatDateTime, fullUserName } from '@hefe/utils';
import { Breadcrumb, Button, Col, Popconfirm, Result, Row } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomTable from '../../../core/components/table/custom-table.component';
import SwitchArticlesPopconfirmComponent from '../../components/switch-articles-popconfirm/switch-articles-popconfirm.component';
import KnowledgeBaseLayout from '../../layout/knowledge-base.layout';

export default function KnowledgeBaseCategoryPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const userIsAdmin = useSelector(currentUserIsAdminSelector);

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [selectedSwitchCategoryId, setSelectedSwitchCategoryId] = useState<
    string | undefined
  >();

  const categoryId = parseInt(id ?? 'a', 10);

  const { data: category, isFetching: isFetchingArticles } =
    KnowledgeBaseApi.useFetchCategoryQuery(categoryId, {
      skip: isNaN(categoryId),
    });

  const [deleteCategory, { isLoading: isDeleting }] =
    KnowledgeBaseApi.useDeleteArticleMutation();

  const [updateCategory, { isLoading: isUpdatingCategory }] =
    KnowledgeBaseApi.useUpdateArticlesCategoryMutation();

  const navigateBack = () => {
    navigate('/knowledge-base');
  };

  const onUpdateCategory = () => {
    const newCategoryId = parseInt(selectedSwitchCategoryId ?? 'a', 10) ?? null;
    updateCategory({
      currentCategoryId: categoryId,
      request: {
        category_id: newCategoryId > 0 ? newCategoryId : null,
        article_ids: selectedRowKeys,
      },
    })
      .unwrap()
      .then(() => {
        setSelectedRowKeys([]);
        setSelectedSwitchCategoryId(undefined);
      })
      .catch(() => undefined);
  };

  const onDelete = (keys: number[], resetKeys?: () => void) => {
    if (keys.length !== 1) {
      return;
    }

    const article = category?.articles?.find(
      (article) => article.id === keys[0]
    );

    if (article) {
      deleteCategory(article)
        .unwrap()
        .then(() => {
          if (resetKeys) {
            resetKeys();
          }
        })
        .catch(() => undefined);
    }
  };

  const breadcrumbs = useMemo(() => {
    return (
      category?.breadcrumbs?.map((breadcrumb) => ({
        key: breadcrumb.id,
        title: breadcrumb.name,
        onClick: () => navigate(`/knowledge-base/categories/${breadcrumb.id}`),
      })) ?? []
    );
  }, [category, navigate]);

  const renderContent = () => {
    if (!isFetchingArticles && !category) {
      return renderNoContent();
    } else {
      return renderArticle();
    }
  };

  const renderNoContent = () => {
    return (
      <Result
        status="404"
        title={t('knowledge_base.categories.article_list.not_found')}
        extra={
          <Button type="primary" onClick={navigateBack}>
            {t('back')}
          </Button>
        }
      />
    );
  };

  const renderArticle = () => {
    return (
      <Row>
        <Col xs={24}>
          <Breadcrumb
            style={{ cursor: 'pointer', marginBottom: 24 }}
            items={[
              {
                key: 'knowledge-base',
                title: t('knowledge_base.title'),
                onClick: navigateBack,
              },
              ...breadcrumbs,
            ]}
          />
          <CustomTable
            name="knowledge-base-category-articles"
            rowKey="id"
            loading={isFetchingArticles}
            rowSelection
            onRowClick={(article: KnowledgeBaseArticle) =>
              navigate(`/knowledge-base/articles/${article.id}`)
            }
            onRowSelect={(keys) => setSelectedRowKeys(keys)}
            columns={[
              {
                key: 'title',
                title: t(
                  'knowledge_base.categories.article_list.columns.title'
                ),
                dataIndex: 'title',
                default: true,
              },
              {
                key: 'created_by',
                title: t(
                  'knowledge_base.categories.article_list.columns.created_by'
                ),
                dataIndex: 'created_by',
                render: (value: string, article: KnowledgeBaseArticle) =>
                  fullUserName(article?.user),
                default: true,
              },
              {
                key: 'created_at',
                title: t(
                  'knowledge_base.categories.article_list.columns.created_at'
                ),
                dataIndex: 'created_at',
                render: (value: string) => formatDateTime(value),
                default: true,
              },
            ]}
            dataSource={category?.articles ?? []}
            deleteAction={
              userIsAdmin
                ? {
                    loading: isDeleting,
                    action: (keys: number[], resetKeys) =>
                      onDelete(keys, resetKeys),
                  }
                : undefined
            }
            displayToolbarItemsFirst
            toolbarItems={
              userIsAdmin
                ? [
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        navigate(`/knowledge-base/articles/new`, {
                          state: { categoryId },
                        })
                      }
                    />,
                    <Popconfirm
                      title={t('knowledge_base.articles.switch_category.title')}
                      description={
                        <SwitchArticlesPopconfirmComponent
                          onSelect={(value) =>
                            setSelectedSwitchCategoryId(value)
                          }
                        />
                      }
                      onConfirm={onUpdateCategory}
                      okButtonProps={{ loading: isUpdatingCategory }}
                      icon={null}
                      disabled={selectedRowKeys.length === 0}
                    >
                      <Button
                        disabled={selectedRowKeys.length === 0}
                        icon={<SwapOutlined />}
                        onClick={() => undefined}
                        loading={isUpdatingCategory}
                      />
                    </Popconfirm>,
                  ]
                : []
            }
          />
        </Col>
      </Row>
    );
  };

  return <KnowledgeBaseLayout>{renderContent()}</KnowledgeBaseLayout>;
}
